/**
 * Products view — Phase 2.3.e.
 *
 * Owns the Products tab: list, search, add/edit/delete dialog, and the
 * enable/disable toggle.
 *
 * What stays in admin.js (injected via `configure({helpers})`):
 *   - `escapeHtml`, `openDialog`, `hasRole`, `filterTable` — generic.
 *   - `getAddProductBtn` — admin.js retains the topbar button. (Phase
 *     2.3.d moved AP onto the same button via the "scan APs" action; the
 *     button itself stays in admin.js.)
 *
 * Phase 3.2.b: dropped the IIFE wrapper; module is now ESM. Foundation
 * modules are pulled in via `import` instead of `window.admin*` reads.
 * The `configure({helpers})` injection pattern stays for now —
 * 3.2.c will replace those helper reads with direct imports.
 */

import { fetch as apiFetch, routes as API } from '../api-client.js';
import { showFlash } from '../notifications.js';
import { escapeHtml, filterTable, formatCurrency } from '../../src/lib/format.js';
import { openDialog } from '../../src/lib/dialog.js';
import { hasRole } from '../../src/lib/roles.js';

const getAddProductBtn = () => document.getElementById('addProductBtn');

// ---- DOM refs (lazy-resolved) ---------------------------------------
let productsTbody, productDlg, productForm, productDlgTitle, productSearchInput;

let domResolved = false;
function resolveDom() {
  if (domResolved) return;
  domResolved = true;
  productsTbody = document.getElementById('productsTbody');
  productDlg = document.getElementById('productDlg');
  productForm = document.getElementById('productForm');
  productDlgTitle = document.getElementById('productDlgTitle');
  productSearchInput = document.getElementById('productSearch');
}

function renderProducts(items = []) {
  productsTbody.innerHTML = '';
  if (!items.length) {
    productsTbody.innerHTML = '<tr><td colspan="6" style="color:var(--sub);">No products</td></tr>';
    return;
  }
  items.forEach((p, idx) => {
    const tr = document.createElement('tr');
    const canWrite = hasRole('root', 'hotspot_admin');
    tr.innerHTML = `
      <td>${idx + 1}</td>
      <td>${escapeHtml(p.name)}</td>
      <td>${escapeHtml(p.description || '')}</td>
      <td>${formatCurrency(p.price)}</td>
      <td>${p.active ? '<span class="badge ok">Enabled</span>' : '<span class="badge off">Disabled</span>'}</td>
      <td>
        ${canWrite ? `<button class="btn" data-act="edit" data-id="${p.id}">✏️ Edit</button>` : ''}
        ${canWrite ? `<button class="btn warn" data-act="toggle" data-id="${p.id}" data-enabled="${p.active}">${p.active ? 'Disable' : 'Enable'}</button>` : ''}
        ${canWrite ? `<button class="btn danger" data-act="delete" data-id="${p.id}">🗑 Delete</button>` : ''}
      </td>`;
    productsTbody.appendChild(tr);
  });
}

async function openProductDialog(id = null) {
  productForm.reset();
  productForm.id.value = id || '';
  productDlgTitle.textContent = id ? 'Edit product' : 'Add product';
  if (id) {
    try {
      const p = await apiFetch(API.product(id), { method: 'GET' });
      productForm.name.value = p.name || '';
      productForm.price.value = p.price || 0;
      productForm.description.value = p.description || '';
      productForm.dlspeed.value = p.dl_speed || 0;
      productForm.ulspeed.value = p.ul_speed || 0;
      productForm.porta_id.value = p.porta_product_id || '';
      productForm.porta_batch_id.value = p.porta_product_batch_id || '';
      productForm.duration.value = p.duration ? Math.round((p.duration || 0) / 60) : 0;
      productForm.enabled.value = String(Boolean(p.active));
    } catch (err) { showFlash(err.message, 'error'); return; }
  }
  openDialog(productDlg);
}

async function loadProducts() {
  try { const data = await apiFetch(API.products, { method: 'GET' }); renderProducts(Array.isArray(data) ? data : (data.items || [])); }
  catch (err) { renderProducts([]); showFlash(err.message, 'error'); }
}

let mounted = false;
export function mount() {
  if (mounted) return;
  mounted = true;
  resolveDom();
  const addProductBtn = getAddProductBtn();
  if (addProductBtn) addProductBtn.addEventListener('click', () => openProductDialog());
  if (productSearchInput) {
    productSearchInput.addEventListener('input', (e) => filterTable('productsTable', e.target.value));
  }
  if (productsTbody) {
    productsTbody.addEventListener('click', async (e) => {
      const btn = e.target.closest('button[data-act]');
      if (!btn) return;
      const id = btn.dataset.id;
      const act = btn.dataset.act;
      if (act === 'edit') return openProductDialog(id);
      if (act === 'toggle') {
        const current = btn.dataset.enabled === 'true';
        if (!confirm(`${current ? 'Disable' : 'Enable'} this product?`)) return;
        try {
          try {
            await apiFetch(API.product(id), { method: 'PATCH', body: JSON.stringify({ active: !current }) });
          } catch (err) {
            if (err.status === 404) await apiFetch(API.productDisable(id), { method: 'POST' }); else throw err;
          }
          showFlash('Product updated');
          loadProducts();
        } catch (err) {
          showFlash(err.message, 'error');
        }
      }
      if (act === 'delete') {
        if (!confirm('Delete this product permanently?')) return;
        try { await apiFetch(API.product(id), { method: 'DELETE' }); showFlash('Product deleted'); loadProducts(); }
        catch (err) { showFlash(err.message, 'error'); }
      }
    });
  }
  if (productForm) {
    productForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const id = productForm.id.value.trim();
      const payload = {
        name: productForm.name.value.trim(),
        price: Number(productForm.price.value),
        ul_speed: Number(productForm.ulspeed.value),
        dl_speed: Number(productForm.dlspeed.value),
        duration: Number(productForm.duration.value) * 60,
        porta_product_id: productForm.porta_id.value.trim(),
        porta_product_batch_id: productForm.porta_batch_id.value.trim(),
        description: productForm.description.value.trim(),
        active: productForm.enabled.value === 'true'
      };
      try {
        if (id) await apiFetch(API.product(id), { method: 'PUT', body: JSON.stringify(payload) });
        else await apiFetch(API.products, { method: 'POST', body: JSON.stringify(payload) });
        productDlg.close();
        showFlash('Product saved');
        loadProducts();
      } catch (err) { showFlash(err.message, 'error'); }
    });
  }
}

export function init() {
  resolveDom();
  loadProducts();
}

export default { mount, init };
